import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { errMsg } from '../../lib/aiInterviewApi';
import { jobsAPI } from '../../lib/api';
import { absUrl, browserTz, fmtSlot, schedulingAPI } from '../../lib/schedulingApi';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { copyText } from '../aiinterview/recruiter/common';
import BusyPreview from './BusyPreview';
import DirectSlots from './DirectSlots';

const INIT = { job_id: '', candidate_id: '', pool_id: '', title: 'Technical interview', duration_min: 45, panel_size: 1, mode: 'self_schedule', venue: 'chire', meeting_url: '', notes: '', window_days: 14, min_notice_hours: 12 };
const Field = ({ label, children, htmlFor }) => (
  <div style={{ display: 'grid', gap: '0.3rem' }}>
    <label className="iv-small" htmlFor={htmlFor} style={{ fontWeight: 700, color: '#002A38' }}>{label}</label>
    {children}
  </div>
);

const Done = ({ b, onClose }) => (
  <div data-testid="schedule-done">
    {b.status === 'scheduled' ? (
      <div className="iv-note is-good">Booked for <b>{fmtSlot(b.start, browserTz())}</b> with {b.interviewers.map((i) => i.name).join(', ')}. {b.invite_mode === 'calendar' ? 'A calendar invite was sent from the interviewer’s calendar.' : 'Email invites with an .ics file are on their way.'}</div>
    ) : (
      <div className="iv-note is-good">{b.candidate_email ? `We emailed ${b.candidate_name} a link to pick a time.` : `${b.candidate_name} has no email on file. Share this link with them.`}</div>
    )}
    {b.schedule_path && (
      <div className="iv-row" style={{ marginTop: '0.75rem' }}>
        <span className="sc-mono" style={{ flex: 1 }} data-testid="schedule-done-link">{absUrl(b.schedule_path)}</span>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => copyText(absUrl(b.schedule_path), toast)} data-testid="schedule-copy-link-btn"><Copy size={14} /> Copy link</button>
      </div>
    )}
    <div className="iv-row-end"><button type="button" className="iv-btn iv-btn-primary" onClick={onClose} data-testid="schedule-done-btn">Done</button></div>
  </div>
);

export default function ScheduleDialog({ open, onOpenChange, pools, preset, onCreated }) {
  const [jobs, setJobs] = useState([]);
  const [cands, setCands] = useState(null);
  const [f, setF] = useState(INIT);
  const [slot, setSlot] = useState(null);
  const [who, setWho] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [video, setVideo] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    setDone(null);
    setSlot(null);
    setF({ ...INIT, job_id: preset?.jobId || '', candidate_id: preset?.candidateId || '', pool_id: pools?.[0]?.id || '' });
    schedulingAPI.me().then(({ data }) => { setVideo(!!data.video_configured); if (!data.video_configured) setF((x) => ({ ...x, venue: 'external' })); }).catch(() => setF((x) => ({ ...x, venue: 'external' })));
    if (!preset?.jobId) jobsAPI.list().then(({ data }) => { setJobs(data || []); setF((x) => ({ ...x, job_id: x.job_id || data?.[0]?._id || data?.[0]?.id || '' })); }).catch(() => toast.error('Could not load jobs'));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !f.job_id || preset?.candidateId) return;
    setCands(null);
    schedulingAPI.jobCandidates(f.job_id).then(({ data }) => { setCands(data); setF((x) => ({ ...x, candidate_id: data?.[0]?.id || '' })); }).catch(() => setCands([]));
  }, [open, f.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await schedulingAPI.create({
        ...f, meeting_url: f.venue === 'chire' ? '' : f.meeting_url, duration_min: Number(f.duration_min), panel_size: Number(f.panel_size), window_days: Number(f.window_days), min_notice_hours: Number(f.min_notice_hours),
        ai_interview_id: preset?.aiInterviewId, candidate_timezone: null,
        ...(f.mode === 'direct' ? { slot_start: slot?.start, interviewer_ids: who ? [who] : undefined } : {}),
      });
      setDone(data);
      onCreated?.();
    } catch (e) {
      toast.error(errMsg(e, 'Could not schedule the interview'));
    } finally {
      setBusy(false);
    }
  };
  const pool = (pools || []).find((p) => p.id === f.pool_id);
  const ready = f.job_id && f.candidate_id && f.pool_id && f.title.trim() && (f.mode !== 'direct' || slot);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="iv sc-dialog max-w-2xl" style={{ maxHeight: '92vh', overflowY: 'auto' }} data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle>Schedule a human interview</DialogTitle>
          <DialogDescription>{preset?.candidateName ? `For ${preset.candidateName}. ` : ''}Send a self-schedule link, or pick a time now. Interviewers come from the pool and are shared out fairly.</DialogDescription>
        </DialogHeader>
        {done ? <Done b={done} onClose={() => onOpenChange(false)} /> : (
          <div className="sc-form">
            {!preset?.jobId && (
              <Field label="Job" htmlFor="sd-job">
                <select id="sd-job" className="iv-select" value={f.job_id} onChange={set('job_id')} data-testid="schedule-job-select">
                  {!jobs.length && <option value="">No jobs yet</option>}
                  {jobs.map((j) => <option key={j._id || j.id} value={j._id || j.id}>{j.title}</option>)}
                </select>
              </Field>
            )}
            {!preset?.candidateId && (
              <Field label="Candidate" htmlFor="sd-cand">
                <select id="sd-cand" className="iv-select" value={f.candidate_id} onChange={set('candidate_id')} data-testid="schedule-candidate-select">
                  {cands === null && <option value="">Loading…</option>}
                  {cands?.length === 0 && <option value="">No candidates on this job yet</option>}
                  {(cands || []).map((c) => <option key={c.id} value={c.id}>{`${c.name || 'Unnamed'}${c.email ? ` · ${c.email}` : ' · no email'}`}</option>)}
                </select>
              </Field>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.8rem' }}>
              <Field label="Interview name" htmlFor="sd-title"><input id="sd-title" className="iv-input" value={f.title} onChange={set('title')} data-testid="schedule-title-input" /></Field>
              <Field label="Pool" htmlFor="sd-pool">
                <select id="sd-pool" className="iv-select" value={f.pool_id} onChange={set('pool_id')} data-testid="schedule-pool-select">
                  {!(pools || []).length && <option value="">Create a pool first</option>}
                  {(pools || []).map((p) => <option key={p.id} value={p.id}>{`${p.name} (${p.members.length})`}</option>)}
                </select>
              </Field>
              <Field label="Duration" htmlFor="sd-dur">
                <select id="sd-dur" className="iv-select" value={f.duration_min} onChange={set('duration_min')} data-testid="schedule-duration-select">
                  {[15, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{`${m} minutes`}</option>)}
                </select>
              </Field>
              <Field label="Interviewers per slot" htmlFor="sd-panel">
                <select id="sd-panel" className="iv-select" value={f.panel_size} onChange={set('panel_size')} data-testid="schedule-panel-select">
                  {[1, 2, 3].filter((n) => n <= (pool?.members.length || 1)).map((n) => <option key={n} value={n}>{`${n}`}</option>)}
                </select>
              </Field>
            </div>
            <div className="iv-options" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <button type="button" className={`iv-option ${f.mode === 'self_schedule' ? 'is-on' : ''}`} onClick={() => setF((x) => ({ ...x, mode: 'self_schedule' }))} data-testid="schedule-mode-self"><b>Candidate picks a time</b><span>Email a private link with open slots</span></button>
              <button type="button" className={`iv-option ${f.mode === 'direct' ? 'is-on' : ''}`} onClick={() => setF((x) => ({ ...x, mode: 'direct' }))} data-testid="schedule-mode-direct"><b>Book a time now</b><span>Pick a slot and send invites</span></button>
            </div>
            {f.mode === 'direct' ? (
              <DirectSlots poolId={f.pool_id} duration={Number(f.duration_min)} panel={Number(f.panel_size)} value={slot} onChange={setSlot} who={who} setWho={setWho} />
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                  <Field label="Link valid for (days)" htmlFor="sd-win"><input id="sd-win" type="number" min={1} max={30} className="iv-input" value={f.window_days} onChange={set('window_days')} data-testid="schedule-window-input" /></Field>
                  <Field label="Minimum notice (hours)" htmlFor="sd-notice"><input id="sd-notice" type="number" min={0} max={72} className="iv-input" value={f.min_notice_hours} onChange={set('min_notice_hours')} data-testid="schedule-notice-input" /></Field>
                </div>
                <BusyPreview poolId={f.pool_id} duration={Number(f.duration_min)} panel={Number(f.panel_size)} notice={Math.min(72, Math.max(0, Number(f.min_notice_hours) || 0))} />
              </>
            )}
            {video && (
              <div className="iv-options" style={{ gridTemplateColumns: '1fr 1fr' }} data-testid="schedule-venue">
                <button type="button" className={`iv-option ${f.venue === 'chire' ? 'is-on' : ''}`} onClick={() => setF((x) => ({ ...x, venue: 'chire' }))} data-testid="schedule-venue-chire"><b>CHIRE video room</b><span>Live copilot, transcript and scorecards</span></button>
                <button type="button" className={`iv-option ${f.venue === 'external' ? 'is-on' : ''}`} onClick={() => setF((x) => ({ ...x, venue: 'external' }))} data-testid="schedule-venue-external"><b>Meet, Teams or own link</b><span>Auto link from a connected calendar, or paste one</span></button>
              </div>
            )}
            {f.venue === 'external' && (
              <Field label="Meeting link (optional)" htmlFor="sd-url">
                <input id="sd-url" className="iv-input" placeholder="Leave empty for an auto Google Meet / Teams link, or paste Zoom etc." value={f.meeting_url} onChange={set('meeting_url')} data-testid="schedule-meeting-input" />
              </Field>
            )}
            <Field label="Note for the candidate (optional)" htmlFor="sd-notes"><textarea id="sd-notes" className="iv-textarea" rows={2} value={f.notes} onChange={set('notes')} data-testid="schedule-notes-input" /></Field>
            <div className="iv-row-end">
              <button type="button" className="iv-btn iv-btn-ghost" onClick={() => onOpenChange(false)}>Cancel</button>
              <button type="button" className="iv-btn iv-btn-cta" disabled={busy || !ready} onClick={submit} data-testid="schedule-submit-btn">{busy ? 'Scheduling…' : f.mode === 'direct' ? 'Book interview' : 'Send scheduling link'}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
